import { normalizeSkillReferenceSnapshots } from "../../types";
import type { SkillReferenceSnapshot, ToolResult } from "../../types";
export {
  parseNarratedReasoningSections,
  type NarratedReasoningSection,
} from "../../../shared/agentReasoning";

/**
 * Hop-indexed agent stream events. `hop` mirrors the `queryOpenAINative`
 * tool-loop iteration that produced the content (0-based). Reasoning/output
 * deltas come from the OpenAI Responses SSE stream; tool_call/tool_result are
 * emitted by the loop (which owns callId + execution status + the result).
 * The `subagent_*` events are produced by the nested finster/factset stream.
 */
export type AgentEvent =
  | { kind: "skills_resolved"; skills: readonly SkillReferenceSnapshot[] }
  | { kind: "reasoning_delta"; hop: number; text: string }
  | { kind: "output_delta"; hop: number; text: string }
  | { kind: "apply_edit_args_delta"; hop: number; text: string }
  | {
      kind: "tool_call";
      hop: number;
      callId: string;
      tool: string;
      argsPreview?: string;
      status: "calling";
    }
  | {
      kind: "tool_result";
      hop: number;
      callId: string;
      tool: string;
      ok: boolean;
      result?: ToolResult;
    }
  | { kind: "tool_progress"; hop: number; callId: string; tool: string; text: string }
  | {
      kind: "subagent_reasoning_delta";
      hop: number;
      callId: string;
      subHop: number;
      text: string;
      sectionTitle?: string;
    }
  | {
      kind: "subagent_tool_call";
      hop: number;
      callId: string;
      subHop: number;
      subCallId: string;
      tool: string;
      status: "calling" | "completed" | "failed";
      description?: string;
      sectionTitle?: string;
    }
  | { kind: "subagent_output_delta"; hop: number; callId: string; text: string }
  | {
      kind: "subagent_result";
      hop: number;
      callId: string;
      answer: string;
      citations?: unknown[];
    }
  | { kind: "status"; hop?: number; text: string };

/** Minimal structural shape of an OpenAI Responses SSE `data:` frame. */
interface RawResponsesEvent {
  type?: string;
  delta?: string;
  name?: string;
  call_id?: string;
  item?: { type?: string; name?: string; call_id?: string };
}

/**
 * Maps a raw OpenAI Responses stream event to agent events, tagging reasoning
 * and answer deltas with the current hop. Tool calls are intentionally NOT
 * mapped here — the loop emits them (it owns callId + execution + result).
 */
export function mapOpenAIStreamEvent(
  event: RawResponsesEvent,
  hop: number,
  options: { includeApplyEditArgsDeltas?: boolean } = {}
): AgentEvent[] {
  switch (event.type) {
    case "response.reasoning_summary_text.delta":
      return event.delta ? [{ kind: "reasoning_delta", hop, text: event.delta }] : [];
    case "response.output_text.delta":
      return event.delta ? [{ kind: "output_delta", hop, text: event.delta }] : [];
    case "response.function_call_arguments.delta":
    case "response.output_item.delta": {
      if (!options.includeApplyEditArgsDeltas || !event.delta) return [];
      return [{ kind: "apply_edit_args_delta", hop, text: event.delta }];
    }
    case "response.output_item.added": {
      // The model has committed to a tool call: surface the pill immediately.
      // name + call_id are known here, BEFORE the (potentially large) arguments
      // finish streaming. The loop re-emits the same callId later with
      // argsPreview; the reducer dedupes/fills by callId, so this only adds the
      // early pill - it never duplicates or loses the later context label.
      const item = event.item;
      if (item?.type === "function_call" && item.name && item.call_id) {
        return [
          { kind: "tool_call", hop, callId: item.call_id, tool: item.name, status: "calling" },
        ];
      }
      return [];
    }
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Timeline view model + reducer (consumed by useAgentStream / AgentTimeline)
// ---------------------------------------------------------------------------

export interface SubAgentHopView {
  subHop: number;
  thinkingText: string;
  tools: SubToolView[];
}

export interface SubToolView {
  subCallId: string;
  tool: string;
  status: "calling" | "completed" | "failed";
  description?: string;
  sectionTitle?: string;
}

export interface SubAgentView {
  hops: SubAgentHopView[];
  liveAnswer: string;
  finalAnswer?: string;
  citations: unknown[];
  status: "running" | "completed" | "failed";
}

export interface ToolView {
  callId: string;
  tool: string;
  argsPreview?: string;
  status: "calling" | "completed" | "failed";
  result?: ToolResult;
  progress?: string[];
  sub?: SubAgentView;
}

export interface HopView {
  hop: number;
  thinkingText: string;
  outputText?: string;
  applyEditArgsText?: string;
  continuationOf?: number;
  hasToolAfterThinking?: boolean;
  tools: ToolView[];
}

export interface AgentTimelineState {
  /** Ordered display-only resolver output; optional for legacy persisted timelines. */
  activeSkills?: SkillReferenceSnapshot[];
  hops: HopView[];
  output: string;
  status?: string;
}

export function emptyTimeline(): AgentTimelineState {
  return { activeSkills: [], hops: [], output: "", status: undefined };
}

function cloneState(state: AgentTimelineState): AgentTimelineState {
  return {
    activeSkills: normalizeSkillReferenceSnapshots(state.activeSkills),
    hops: state.hops.map((hop) => ({
      ...hop,
      tools: hop.tools.map((tool) => ({
        ...tool,
        progress: tool.progress ? [...tool.progress] : undefined,
        sub: tool.sub
          ? {
              ...tool.sub,
              citations: [...tool.sub.citations],
              hops: tool.sub.hops.map((subHop) => ({
                ...subHop,
                tools: subHop.tools.map((subTool) => ({ ...subTool })),
              })),
            }
          : undefined,
      })),
    })),
    output: state.output,
    status: state.status,
  };
}

export function getActiveSkillReferences(
  state: AgentTimelineState | undefined
): SkillReferenceSnapshot[] {
  return normalizeSkillReferenceSnapshots(state?.activeSkills);
}

export function hasAgentTimelineActivity(state: AgentTimelineState | undefined): boolean {
  return getActiveSkillReferences(state).length > 0 || (state?.hops?.length ?? 0) > 0;
}

/**
 * Hops the timeline actually draws. `skills_resolved` reserves an empty hop
 * zero, so raw `hops.length` reports progress a reader cannot see.
 */
export function visibleAgentHops(state: AgentTimelineState | undefined): HopView[] {
  return (state?.hops ?? []).filter(
    (hop) => hop.thinkingText.trim().length > 0 || hop.tools.length > 0
  );
}

function ensureHop(state: AgentTimelineState, hop: number): HopView {
  let view = state.hops.find((h) => h.hop === hop);
  if (!view) {
    view = { hop, thinkingText: "", tools: [] };
    state.hops.push(view);
    state.hops.sort((a, b) => a.hop - b.hop);
  }
  return view;
}

function ensureContinuationHopAfter(state: AgentTimelineState, hop: number): HopView {
  const existing = state.hops.find(
    (view) => view.continuationOf === hop && view.tools.length === 0
  );
  if (existing) return existing;

  const nextHop = Math.max(hop + 1, ...state.hops.map((view) => view.hop + 1));
  const view = ensureHop(state, nextHop);
  view.continuationOf = hop;
  return view;
}

function splitAtNarratorSectionBoundary(text: string): [string, string] {
  const boundaryMatch = /\n\s*\n\s*(?=\*\*[^*\n][\s\S]*?\*\*)/.exec(text);
  if (!boundaryMatch) return [text, ""];
  return [text.slice(0, boundaryMatch.index), text.slice(boundaryMatch.index)];
}

function shouldContinueOpenNarratorSentence(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.endsWith("**")) return false;
  return !/[.!?…:;"')\]]$/.test(trimmed);
}

function findTool(state: AgentTimelineState, callId: string): ToolView | undefined {
  for (const hop of state.hops) {
    const tool = hop.tools.find((t) => t.callId === callId);
    if (tool) return tool;
  }
  return undefined;
}

function ensureSub(tool: ToolView): SubAgentView {
  if (!tool.sub) {
    tool.sub = { hops: [], liveAnswer: "", citations: [], status: "running" };
  }
  return tool.sub;
}

function ensureSubHop(sub: SubAgentView, subHop: number): SubAgentHopView {
  let view = sub.hops.find((h) => h.subHop === subHop);
  if (!view) {
    view = { subHop, thinkingText: "", tools: [] };
    sub.hops.push(view);
    sub.hops.sort((a, b) => a.subHop - b.subHop);
  }
  return view;
}

/**
 * Pure reducer: folds an `AgentEvent` into the timeline view model. Returns a
 * new top-level object (with cloned hops/tools) so React re-renders on change.
 */
export function reduceAgentEvent(prev: AgentTimelineState, event: AgentEvent): AgentTimelineState {
  const state = cloneState(prev);

  switch (event.kind) {
    case "skills_resolved": {
      state.activeSkills = normalizeSkillReferenceSnapshots(event.skills);
      if (state.activeSkills.length > 0 && state.hops.length === 0) {
        // Existing live integrations gate their pane on `hops.length`. Reserve
        // hop zero so Skill activity is visible before the first model token;
        // AgentTimeline omits this row until it gains reasoning or a tool.
        ensureHop(state, 0);
      }
      break;
    }
    case "reasoning_delta": {
      const hop = ensureHop(state, event.hop);
      if (hop.tools.length > 0 && hop.hasToolAfterThinking) {
        if (shouldContinueOpenNarratorSentence(hop.thinkingText)) {
          const [sentenceContinuation, nextSection] = splitAtNarratorSectionBoundary(event.text);
          hop.thinkingText += sentenceContinuation;
          if (nextSection) {
            ensureContinuationHopAfter(state, event.hop).thinkingText += nextSection;
          }
        } else {
          ensureContinuationHopAfter(state, event.hop).thinkingText += event.text;
        }
      } else {
        hop.thinkingText += event.text;
      }
      break;
    }
    case "output_delta":
      state.output += event.text;
      ensureHop(state, event.hop).outputText =
        (ensureHop(state, event.hop).outputText ?? "") + event.text;
      break;
    case "apply_edit_args_delta":
      ensureHop(state, event.hop).applyEditArgsText =
        (ensureHop(state, event.hop).applyEditArgsText ?? "") + event.text;
      break;
    case "tool_call": {
      const hop = ensureHop(state, event.hop);
      if (hop.thinkingText.trim().length > 0) {
        hop.hasToolAfterThinking = true;
      }
      const existing = hop.tools.find((t) => t.callId === event.callId);
      if (existing) {
        // The early pill (from output_item.added) created this tool without
        // args; the loop's later emit carries argsPreview (e.g. the research
        // query). Fill it in without duplicating the pill.
        if (event.argsPreview) existing.argsPreview = event.argsPreview;
        if (event.tool) existing.tool = event.tool;
      } else {
        hop.tools.push({
          callId: event.callId,
          tool: event.tool,
          argsPreview: event.argsPreview,
          status: "calling",
        });
      }
      break;
    }
    case "tool_result": {
      const tool = findTool(state, event.callId);
      if (tool) {
        tool.status = event.ok ? "completed" : "failed";
        tool.result = event.result;
        if (tool.sub && tool.sub.status === "running") {
          tool.sub.status = event.ok ? "completed" : "failed";
        }
      }
      break;
    }
    case "tool_progress": {
      let tool = findTool(state, event.callId);
      if (!tool) {
        const hop = ensureHop(state, event.hop);
        tool = {
          callId: event.callId,
          tool: event.tool,
          status: "calling",
          progress: [],
        };
        hop.tools.push(tool);
      }
      tool.progress = tool.progress ?? [];
      if (tool.progress[tool.progress.length - 1] !== event.text) {
        tool.progress.push(event.text);
      }
      break;
    }
    case "subagent_reasoning_delta": {
      const tool = findTool(state, event.callId);
      if (tool) ensureSubHop(ensureSub(tool), event.subHop).thinkingText += event.text;
      break;
    }
    case "subagent_tool_call": {
      const tool = findTool(state, event.callId);
      if (tool) {
        const subHop = ensureSubHop(ensureSub(tool), event.subHop);
        const existing = subHop.tools.find((t) => t.subCallId === event.subCallId);
        if (existing) {
          existing.status = event.status;
          existing.description = event.description ?? existing.description;
        } else {
          subHop.tools.push({
            subCallId: event.subCallId,
            tool: event.tool,
            status: event.status,
            description: event.description,
            sectionTitle: event.sectionTitle,
          });
        }
      }
      break;
    }
    case "subagent_output_delta": {
      const tool = findTool(state, event.callId);
      if (tool) ensureSub(tool).liveAnswer += event.text;
      break;
    }
    case "subagent_result": {
      const tool = findTool(state, event.callId);
      if (tool) {
        const sub = ensureSub(tool);
        sub.finalAnswer = event.answer;
        sub.citations = event.citations ?? [];
        if (sub.status === "running") sub.status = "completed";
      }
      break;
    }
    case "status":
      state.status = event.text;
      break;
  }

  return state;
}

/**
 * Progressive extraction of the terminal chat answer from the streaming
 * structured output. The agentic terminal schema is `{ mode: "chat", reply }`
 * (legacy probes used `chatResponse`), so we scan the growing JSON for the
 * first matching key and read its (possibly partial) string value. Returns ""
 * until the field appears.
 */
export function extractTerminalReply(jsonText: string): string {
  for (const key of ['"reply":"', '"chatResponse":"']) {
    const start = jsonText.indexOf(key);
    if (start === -1) continue;
    let out = "";
    let cursor = start + key.length;
    while (cursor < jsonText.length) {
      const char = jsonText[cursor];
      if (char === "\\") {
        out += char + (jsonText[cursor + 1] ?? "");
        cursor += 2;
        continue;
      }
      if (char === '"') break;
      out += char;
      cursor += 1;
    }
    try {
      return JSON.parse(`"${out}"`) as string;
    } catch {
      return out;
    }
  }
  return "";
}

/**
 * Bounds a timeline for persistence: caps hop/sub-hop counts and truncates
 * reconstructed thinking text. Raw per-token deltas are never stored (only the
 * reduced text), so this keeps localStorage healthy for large multi-hop runs.
 */
export function capAgentTimeline(
  state: AgentTimelineState,
  opts?: { maxHops?: number; maxThinkingChars?: number }
): AgentTimelineState {
  const maxHops = opts?.maxHops ?? 24;
  const maxChars = opts?.maxThinkingChars ?? 4000;
  const trim = (text: string) => (text.length > maxChars ? `${text.slice(0, maxChars)}…` : text);
  const hops = state.hops.slice(0, maxHops).map((hop) => ({
    hop: hop.hop,
    thinkingText: trim(hop.thinkingText),
    outputText: hop.outputText ? trim(hop.outputText) : undefined,
    continuationOf: hop.continuationOf,
    hasToolAfterThinking: hop.hasToolAfterThinking,
    tools: hop.tools.map((tool) => ({
      ...tool,
      sub: tool.sub
        ? {
            ...tool.sub,
            liveAnswer: "",
            hops: tool.sub.hops.slice(0, maxHops).map((subHop) => ({
              ...subHop,
              thinkingText: trim(subHop.thinkingText),
            })),
          }
        : undefined,
    })),
  }));
  return {
    activeSkills: normalizeSkillReferenceSnapshots(state.activeSkills),
    hops,
    output: trim(state.output),
    status: state.status,
  };
}
